import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const menuItems = [
  { icon: "ri-dashboard-line", label: "控制台", path: "/tenant/dashboard" },
  { icon: "ri-global-line", label: "站点管理", path: "/tenant/sites" },
  { icon: "ri-team-line", label: "用户管理", path: "/tenant/users" },
  { icon: "ri-vip-crown-line", label: "套餐管理", path: "/tenant/plans" },
  { icon: "ri-user-settings-line", label: "账户信息", path: "/tenant/account" },
];

interface TenantLayoutProps {
  children: React.ReactNode;
}

export default function TenantLayout({ children }: TenantLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const currentLabel =
    menuItems.find((m) => location.pathname === m.path || location.pathname.startsWith(m.path + "/"))?.label || "租户中心";

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-gray-900 text-white transition-all duration-300 z-40 flex flex-col ${
          sidebarCollapsed ? "w-16" : "w-56"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-700/50 shrink-0">
          <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg shrink-0">
            <i className="ri-building-2-line text-white text-base" />
          </div>
          {!sidebarCollapsed && (
            <div>
              <div className="text-sm font-bold text-white">租户中心</div>
              <div className="text-xs text-gray-400">Tenant Portal</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            return (
              <Link
                key={item.path}
                to={item.path}
                title={sidebarCollapsed ? item.label : undefined}
                className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg mb-1 transition-colors ${
                  isActive
                    ? "bg-orange-500 text-white"
                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base`} />
                </div>
                {!sidebarCollapsed && (
                  <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-gray-700/50 p-3 shrink-0 space-y-1">
          <Link
            to="/admin"
            title={sidebarCollapsed ? "返回后台" : undefined}
            className="flex items-center gap-3 px-2 py-2 w-full rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-arrow-go-back-line text-base" />
            </div>
            {!sidebarCollapsed && <span className="text-sm">返回后台</span>}
          </Link>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="flex items-center gap-3 px-2 py-2 w-full rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`${sidebarCollapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} text-base`} />
            </div>
            {!sidebarCollapsed && <span className="text-sm">收起菜单</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? "ml-16" : "ml-56"}`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 h-14 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/admin" className="hover:text-orange-500 transition-colors flex items-center gap-1">
              <i className="ri-home-4-line" />
              <span>后台首页</span>
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/tenant/dashboard" className="hover:text-orange-500 transition-colors">租户中心</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-800 font-medium">{currentLabel}</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-building-2-line text-orange-500 text-sm" />
              </div>
              <span className="text-sm font-medium text-orange-600">{user?.nickname}</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-r-line text-sm" />
              </div>
              <span>退出</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}